import type { FC } from "react";
import { useMemo } from "react";
import styled from "@emotion/styled";
import { BodyText, Heading4 } from "../../primitives/typography";
import { breakpoints, responsiveSpacing, spacing } from "../../design-tokens";
import { SuggestedQuestions } from "./suggested-questions";
import { CHAT_MODE, useChatState } from "@/store/chat-state";
import { useOptionalWidgetRuntimeConfig } from "@/context/widget-runtime-config";
import type { FollowUpQuestion } from "@/types/chat-parts";

const LandingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
  gap: ${spacing["6xl"]}px;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.md}px) {
    margin-top: ${responsiveSpacing.landingTopMargin.mobile}px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.md}px;
  text-align: center;
  box-sizing: border-box;
`;

const FALLBACK_LANDING_COPY
  = "We'll search trusted content to help you find the right information.";

export const Landing: FC = () => {
  const { suggestedQuestions, setUserQuery, setMode, setContextUrl }
    = useChatState();
  const runtimeConfig = useOptionalWidgetRuntimeConfig();
  const showChatQuestions = runtimeConfig?.showChatQuestions ?? true;

  // Tenant-provided suggested questions are authoritative, including an empty
  // list. Rendering outside the runtime provider also defaults to no questions.
  const resolvedQuestions: FollowUpQuestion[] = useMemo(() => {
    const tenantQuestions = runtimeConfig?.widgetSuggestedQuestions;
    if (tenantQuestions) {
      return tenantQuestions.map(question => ({ question }));
    }
    return suggestedQuestions ?? [];
  }, [runtimeConfig?.widgetSuggestedQuestions, suggestedQuestions]);

  if (!showChatQuestions || resolvedQuestions.length === 0) {
    return null;
  }

  const handleQuestionClick = (question: string) => {
    setContextUrl(undefined);
    setMode(CHAT_MODE.INPUT);
    setUserQuery(question);
  };

  return (
    <LandingContainer>
      <HeaderSection>
        <Heading4>Popular questions</Heading4>
        <BodyText textAlign="center">{FALLBACK_LANDING_COPY}</BodyText>
      </HeaderSection>

      <SuggestedQuestions
        questions={resolvedQuestions}
        onQuestionClick={handleQuestionClick}
      />
    </LandingContainer>
  );
};
